import Web3 from 'web3'
import { db, sleep, coerceChainId } from '../utils.mjs'
import {getChainFromId, buildWalletAddEthereumChainParams} from './config.mjs'
import { applyWalletSession } from '../walletSession.mjs'
import { isWalletConnectProvider } from '../walletLifecycle.mjs'
import { dispatchEvmAccountsChanged, dispatchEvmChainChanged } from '../evmInjectHooks.mjs'
import {
  TRON_CHAIN_ID,
  isTronChainId,
  isTronPageContext,
  isTronContext,
  isTronInjectAvailable,
  isTronDappBrowser,
  isTronUiContext,
  isTronInjectWalletAvailable,
  resolveTronInjectSessionKey,
  resolveTronWalletSessionKey,
  inferTronWalletKeyFromProvider,
  connectTronAccount,
  waitForTronInjectProbe,
  resolveTronWeb,
  resolveTronLink,
  TRON_WC_FALLBACK_KEYS,
  TRON_UI_WALLET_KEYS,
  TRON_INJECT_KEYS,
} from './tronInject.mjs'
import { requireTronWeb, isTronWebProvider } from './tron.mjs'
import { isTronWalletConnectProvider, TRON_WC_WALLET_TYPE } from '../tronWcDetect.mjs'
import { isMetaMaskTronProvider } from './metamaskTron.mjs'
import {
  initInjectProbe,
  getEip6963Entries,
  pickEip6963ProviderForWallet,
  requestInjectProbeRefresh,
} from './injectProbe.mjs'

export {
  TRON_CHAIN_ID,
  isTronChainId,
  isTronPageContext,
  isTronContext,
  isTronInjectAvailable,
  isTronDappBrowser,
  isTronUiContext,
  isTronInjectWalletAvailable,
  resolveTronInjectSessionKey,
  resolveTronWalletSessionKey,
  inferTronWalletKeyFromProvider,
  resolveTronLink,
  TRON_WC_FALLBACK_KEYS,
  TRON_UI_WALLET_KEYS,
  TRON_INJECT_KEYS,
}

function collectInjectedProvidersLegacy() {
  const out = []
  const seen = new Set()
  const pushUnique = (p) => {
    if (!p || typeof p.request !== 'function') return
    if (seen.has(p)) return
    seen.add(p)
    out.push(p)
  }
  try {
    const eth = window.ethereum
    if (eth && Array.isArray(eth.providers) && eth.providers.length) {
      eth.providers.forEach(pushUnique)
    }
    pushUnique(eth)
  } catch (_e) {
    void _e
  }
  try {
    pushUnique(window.bitkeep && window.bitkeep.ethereum)
  } catch (_e) {
    void _e
  }
  try {
    pushUnique(window.avewallet && window.avewallet.ethereum)
    pushUnique(window.aveWallet && window.aveWallet.ethereum)
    pushUnique(window.ave && window.ave.ethereum)
  } catch (_e) {
    void _e
  }
  try {
    pushUnique(window.okxwallet)
    pushUnique(window.trustwallet?.ethereum)
    pushUnique(window.coinbaseWalletExtension)
    pushUnique(window.BinanceChain?.ethereum)
    pushUnique(window.binancew3w?.ethereum)
  } catch (_e) {
    void _e
  }
  return out
}

function collectInjectedProviders() {
  initInjectProbe()
  const out = []
  const seen = new Set()
  const pushUnique = (p) => {
    if (!p || typeof p.request !== 'function') return
    if (seen.has(p)) return
    seen.add(p)
    out.push(p)
  }
  for (const entry of getEip6963Entries()) pushUnique(entry.provider)
  for (const p of collectInjectedProvidersLegacy()) pushUnique(p)
  return out
}

function isLikelyMetamaskOnly(provider) {
  return (
    !!provider?.isMetaMask &&
    !provider.isTokenPocket &&
    !(provider.isBitKeep || provider.isBitgetWallet) &&
    !isAveInjectedProvider(provider) &&
    !(provider.isTrust || provider.isTrustWallet) &&
    !provider.isCoinbaseWallet &&
    !(provider.isOkxWallet || provider.isOKExWallet) &&
    !(provider.isBinance || provider.isBinanceWallet)
  )
}

export function listInjectedProviders() {
  return collectInjectedProviders()
}

export function isAveInjectedProvider(provider) {
  if (!provider || typeof provider !== 'object') return false
  if (provider.isAveWallet || provider.isAVEWallet || provider.isAVE || provider.isAve) return true
  if (typeof provider.providerName === 'string' && /avewallet|ave\.ai/i.test(provider.providerName)) return true
  const wn = String(provider.walletName || '')
  if (wn && /avewallet|ave\.ai|ave wallet/i.test(wn)) return true
  return false
}

export function inferWalletKeyFromProvider(provider) {
  if (!provider) return ''
  const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '').toLowerCase()
  if (
    isAveInjectedProvider(provider) ||
    /ave\.ai|avewallet|ave\s*wallet/i.test(ua) ||
    (typeof window !== 'undefined' && (window.avewallet || window.aveWallet || window.ave))
  ) return 'ave'
  if (provider.isTokenPocket) return 'tokenPocket'
  if (provider.isBitKeep || provider.isBitgetWallet) return 'bitget'
  if (provider.isOkxWallet || provider.isOKExWallet) return 'okx'
  if (provider.isCoinbaseWallet) return 'coinbase'
  if (provider.isTrust || provider.isTrustWallet) return 'trust'
  if (provider.isBinance || provider.isBinanceWallet) return 'binance'
  if (provider.isMetaMask) return 'metamask'
  return ''
}

export function pickInjectProviderByWallet(wallet) {
  const key = String(wallet?.key || '')
  const from6963 = pickEip6963ProviderForWallet(key)
  if (from6963 && providerMatchesWalletKey(from6963, key)) return from6963
  const providers = listInjectedProviders()
  if (!providers.length) return null
  const checks = {
    metamask: (p) => isLikelyMetamaskOnly(p),
    bitget: (p) => !!(p.isBitKeep || p.isBitgetWallet),
    tokenPocket: (p) => !!p.isTokenPocket,
    ave: (p) => isAveInjectedProvider(p),
    trust: (p) => !!(p.isTrust || p.isTrustWallet),
    okx: (p) => !!(p.isOkxWallet || p.isOKExWallet),
    coinbase: (p) => !!p.isCoinbaseWallet,
    binance: (p) => !!(p.isBinance || p.isBinanceWallet),
  }
  const check = checks[key]
  if (!check) return null
  return providers.find((p) => check(p) && providerMatchesWalletKey(p, key)) || null
}

/** 所选钱包 key 与注入 provider 是否一致（避免 MM/TP 等同页误匹配） */
export function providerMatchesWalletKey(provider, walletKey) {
  const key = String(walletKey || '').trim()
  if (!provider || !key) return false
  const inferred = inferWalletKeyFromProvider(provider)
  if (inferred) return inferred === key
  if (key === 'metamask') return isLikelyMetamaskOnly(provider)
  if (key === 'tokenPocket') return !!provider.isTokenPocket
  return false
}

async function ensureEvmProviderOnChain(provider, targetChainId) {
  const target = Number(targetChainId)
  if (!provider?.request || !getChainFromId(target) || isTronChainId(target)) return target
  const readChain = async () => {
    const raw = await provider.request({ method: 'eth_chainId' })
    return coerceChainId(raw, target)
  }
  let current = await readChain()
  if (current === target) return target
  const hex = '0x' + target.toString(16)
  try {
    await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hex }] })
  } catch (e) {
    const code = e?.code
    const msg = String(e?.message || e || '').toLowerCase()
    const needAdd =
      code === 4902 ||
      code === -32603 ||
      msg.includes('unrecognized chain') ||
      msg.includes('not added') ||
      msg.includes('not configured') ||
      msg.includes('wrong network')
    if (!needAdd) throw e
    const params = buildWalletAddEthereumChainParams(target)
    if (!params) throw e
    await provider.request({ method: 'wallet_addEthereumChain', params: [params] })
    await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hex }] })
  }
  current = await readChain()
  return current === target ? target : (getChainFromId(current) ? current : target)
}

/** 各钱包官网 / 下载页（未检测到扩展时引导安装，参考 Pancake） */
export const WALLET_INSTALL_URLS = {
  metamask: 'https://metamask.io/download/',
  tokenPocket: 'https://www.tokenpocket.pro/en/download/app',
  bitget: 'https://web3.bitget.com/en/wallet-download',
  ave: 'https://ave.ai/',
  tronLink: 'https://www.tronlink.org/',
  trust: 'https://trustwallet.com/browser-extension',
  okx: 'https://www.okx.com/download',
  coinbase: 'https://www.coinbase.com/wallet/downloads',
  binance: 'https://www.binance.com/en/web3wallet',
}

export function getWalletInstallUrl(walletKey) {
  return WALLET_INSTALL_URLS[String(walletKey || '')] || ''
}

export function isInjectWalletAvailable(wallet) {
  return !!pickInjectProviderByWallet(wallet)
}

export { requestInjectProbeRefresh, initInjectProbe } from './injectProbe.mjs'

/** 小狐狸兼容的 EIP-1193 节点：优先 isMetaMask，否则任意可用注入 */
export function pickMetamaskCompatibleProvider() {
  const providers = listInjectedProviders()
  const mm = providers.find((p) => isLikelyMetamaskOnly(p))
  if (mm) return mm
  const generic = getProvider()
  if (generic && typeof generic.request === 'function') return generic
  return providers[0] || null
}

/**
 * 连接注入钱包：仅精确匹配所选钱包，未检测到则返回 null（由 UI 引导安装官网）。
 * @returns {{ provider: object|null, sessionKey: string }}
 */
export function resolveInjectProviderForWallet(wallet) {
  const key = String(wallet?.key || '')
  const exact = pickInjectProviderByWallet(wallet)
  return { provider: exact || null, sessionKey: key }
}

export function inferWalletLabelFromProvider(provider) {
  if (!provider) return 'Injected Wallet'
  const key = inferWalletKeyFromProvider(provider)
  if (key === 'ave') return 'AVE Wallet'
  if (key === 'tokenPocket') return 'TokenPocket'
  if (key === 'bitget') return 'Bitget Wallet'
  if (key === 'okx') return 'OKX Wallet'
  if (key === 'coinbase') return 'Coinbase Wallet'
  if (key === 'trust') return 'Trust Wallet'
  if (key === 'binance') return 'Binance Wallet'
  if (key === 'metamask') return 'MetaMask'
  return 'Injected Wallet'
}

function persistInjectedWalletLabelIfConfident(provider) {
  applyWalletKeyFromProvider(provider)
}

/** 根据注入 provider 同步 wallet_key（EVM 静默连接 / 自动回填图标） */
export function applyWalletKeyFromProvider(provider) {
  try {
    const key = inferWalletKeyFromProvider(provider)
    if (!key) return ''
    const prevKey = String(db.get('wallet_key', '') || '').trim()
    if (prevKey === key) return key
    // tronLink 仅为 Tron 协议占位；EVM 连接时以实际 provider（如 TP）为准
    if (!prevKey || prevKey === 'tronLink' || prevKey === 'metamask') {
      applyWalletSession(key)
      return key
    }
    return prevKey
  } catch (_e) {
    return ''
  }
}

export async function getCurrentInjectedProvider() {
  const providers = collectInjectedProviders()
  if (!providers.length) return null
  for (const p of providers) {
    try {
      const accounts = await p.request({ method: 'eth_accounts' })
      if (Array.isArray(accounts) && accounts.length > 0) return p
    } catch (_e) {
      void _e
    }
  }
  // 如果无法直接探测“当前激活钱包”，退回首个可用注入 provider
  return providers[0]
}

export function getProvider(){
  const providers = collectInjectedProviders()
  if (providers.length) return providers[0]
  return (window.bitkeep && window.bitkeep.ethereum) || window.ethereum
}

function normalizeEvmAddress(addr) {
  const raw = String(addr || '').trim()
  if (!raw) return ''
  try {
    return new Web3().utils.toChecksumAddress(raw)
  } catch (_e) {
    return raw.startsWith('0x') ? raw.toLowerCase() : raw
  }
}

/** Tron 签名/发交易用 provider：WC / MetaMask Tron 保持原对象，不回落 TronLink */
export async function resolveTronSigningProvider(provider) {
  if (isTronWalletConnectProvider(provider)) return provider
  if (isMetaMaskTronProvider(provider)) return provider
  if (isTronWebProvider(provider)) return provider
  if (db.get('wallet_type', 'inject') === TRON_WC_WALLET_TYPE) {
    try {
      const wc = await import('./walletconnect-tron.mjs')
      const active = wc.getActiveTronWalletConnectProvider()
      if (active) return active
      const restored = await wc.tryRestoreTronWalletConnect()
      if (restored?.provider) return restored.provider
    } catch (_e) {
      void _e
    }
  }
  return requireTronWeb()
}

/**
 * 发交易前从钱包读取当前可签名账户，避免 store 缓存地址与钱包选中账户不一致导致 invalid sender。
 */
export async function resolveTronTxSender(provider, expectedAccount) {
  const acc = String(expectedAccount || '').trim()
  if (!acc) {
    const err = new Error('NO_TRON_ACCOUNT')
    err.code = 'NO_PROVIDER'
    throw err
  }
  const signingProvider = await resolveTronSigningProvider(provider)
  return { provider: signingProvider, account: acc, syncedFromWallet: false }
}

export async function resolveWalletSender(provider, expectedAccount, chainId) {
  if (Number(chainId) === TRON_CHAIN_ID) {
    return resolveTronTxSender(provider, expectedAccount)
  }
  return resolveTxSender(provider, expectedAccount)
}

export async function resolveTxSender(provider, expectedAccount) {
  let p =
    provider && typeof provider.request === 'function' ? provider : null
  if (db.get('wallet_type') === 'wallet_connect') {
    const wc = await import('./walletconnect.mjs')
    p = wc.resolveEvmWalletConnectProvider(p)
  } else {
    if (!p) p = await getCurrentInjectedProvider()
    if (!p) p = getProvider()
  }
  if (!p?.request) {
    const err = new Error('NO_EIP1193_PROVIDER')
    err.code = 'NO_PROVIDER'
    throw err
  }

  let accounts = []
  try {
    accounts = await p.request({ method: 'eth_accounts' })
  } catch (_e) {
    void _e
  }
  if (!Array.isArray(accounts) || !accounts[0]) {
    if (db.get('wallet_type') === 'wallet_connect') {
      try {
        const wc = await import('./walletconnect.mjs')
        const cid = coerceChainId(db.get('chainId', 0), 0)
        const fromSession = wc.extractWalletConnectAccount(p, cid)
        if (fromSession) accounts = [fromSession]
      } catch (_e2) {
        void _e2
      }
      if (!accounts?.[0]) {
        accounts = await Promise.race([
          p.request({ method: 'eth_accounts' }),
          sleep(8000).then(() => []),
        ])
      }
    } else {
      accounts = await p.request({ method: 'eth_requestAccounts' })
    }
  }
  const sender = normalizeEvmAddress(accounts?.[0])
  if (!sender) {
    const err = new Error('NO_EIP1193_PROVIDER')
    err.code = 'NO_PROVIDER'
    throw err
  }

  const expected = normalizeEvmAddress(expectedAccount)
  if (expected && expected.toLowerCase() !== sender.toLowerCase()) {
    console.warn('[hieswap] wallet account differs from page, using wallet account', {
      page: expected,
      wallet: sender,
    })
    return { provider: p, account: sender, syncedFromWallet: true }
  }
  return { provider: p, account: sender, syncedFromWallet: false }
}
function getTronWeb(){
  return resolveTronWeb(typeof globalThis !== 'undefined' ? globalThis : {})
}
function getTronLink(){
  return resolveTronLink(typeof globalThis !== 'undefined' ? globalThis : {})
}

export async function getAccountTronWeb(options = {}){
  const walletKey = String(options.walletKey || '').trim()
  if (walletKey) {
    await waitForTronInjectProbe({ timeoutMs: 900 })
  }
  const res = await connectTronAccount({
    walletKey,
    tronOnly: !!options.tronOnly || !!walletKey,
    requestTimeoutMs: options.requestTimeoutMs ?? 18000,
    timeoutMs: options.timeoutMs ?? 8000,
  })
  db.set('wallet_type', 'inject')
  db.set('topic', '')
  if (options.applySession !== false) {
    applyWalletSession(resolveTronWalletSessionKey(options.walletKey, res.provider))
  }
  return res
}
export function attachEvmInjectProviderListeners(provider) {
  if (!provider || typeof provider.on !== 'function') return
  if (isWalletConnectProvider(provider)) return
  try {
    if (typeof provider.removeAllListeners === 'function') {
      provider.removeAllListeners()
    }
  } catch (_e) {
    void _e
  }
  provider.on('accountsChanged', (accs) => {
    if (dispatchEvmAccountsChanged(accs)) return
    location.reload()
  })
  provider.on('chainChanged', (e) => {
    console.log('chainChanged', e)
    if (dispatchEvmChainChanged(e)) return
    const nextChainId = coerceChainId(e, 0)
    if (getChainFromId(nextChainId)) {
      db.set('chainId', nextChainId)
    }
    location.reload()
  })
}

export async function getAccountInject(chainId, force = false, options = {}) {
  const evmOnly = !!options.evmOnly
  const selectedProvider = options.provider || (options.preferCurrent ? await getCurrentInjectedProvider() : null)
  const provider = selectedProvider || getProvider()
  const fromTron = location.search.indexOf('fromTron') != -1
  const tronWeb = getTronWeb()
  const targetChainId = Number(chainId)

  if (
    !evmOnly
    && (
      isTronContext(chainId)
      || fromTron
      || chainId == TRON_CHAIN_ID
      || (window.isBitKeep && tronWeb && tronWeb.defaultAddress && tronWeb.defaultAddress.hex)
    )
  ) {
    try {
      return await getAccountTronWeb()
    } catch (e) {
      console.warn('[hieswap] getAccountTronWeb failed', e)
      if (isTronContext(chainId) || chainId == TRON_CHAIN_ID || fromTron) {
        throw e
      }
    }
  }
  if (!provider) {
    throw 'can not find web3 provider'
  }
  let accounts
  try {
    accounts = await provider.request({ method: 'eth_requestAccounts' })
  } catch (e) {
    if (!evmOnly && window.okxwallet) return await getAccountTronWeb()
    throw e
  }
  const account = accounts[0]
  if (String(account || '').startsWith('T')) {
    if (!evmOnly && window.okxwallet) return await getAccountTronWeb()
    const err = new Error('EVM_WALLET_REQUIRED')
    err.code = 'EVM_WALLET_REQUIRED'
    throw err
  }

  let resolvedChainId = targetChainId
  if (getChainFromId(targetChainId) && !isTronChainId(targetChainId)) {
    resolvedChainId = await ensureEvmProviderOnChain(provider, targetChainId)
  } else {
    const providerChainId = coerceChainId(await provider.request({ method: 'eth_chainId' }), targetChainId)
    resolvedChainId = isTronChainId(targetChainId) ? providerChainId : providerChainId
  }

  if (force && resolvedChainId !== targetChainId && getChainFromId(targetChainId) && !isTronChainId(targetChainId)) {
    resolvedChainId = await ensureEvmProviderOnChain(provider, targetChainId)
  }

  chainId = resolvedChainId
  attachEvmInjectProviderListeners(provider)
  db.set('wallet_type', 'inject')
  db.set('topic', '')
  persistInjectedWalletLabelIfConfident(provider)
  return { account, chainId, provider }
}